import { defineStore } from "pinia";
import { ref } from "vue";
import { type Message } from "@lr-notitia/notitia-alternis/schemas";
import { type DescriptorNotification } from "@lr-notitia/notitia-operator/schemas";
import { api } from "@lr-notitia/notitia-operator/client";
import { useDomainStore } from "../domain";
import { useQuestionsStore } from "../questions";
import { useGraphStore } from "../graph";
import { useSymbolsStore } from "../symbols";

let domainStore: ReturnType<typeof useDomainStore>;
let questionsStore: ReturnType<typeof useQuestionsStore>;
let graphStore: ReturnType<typeof useGraphStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;

function init() {
	domainStore = useDomainStore();
	questionsStore = useQuestionsStore();
	graphStore = useGraphStore();
	symbolsStore = useSymbolsStore();
}

// --- Temporary Quick Hack
export const useGptQuestionsSchemaSpecializationStore = defineStore("gpt-questions-schema-specialization", () => {
	if (!symbolsStore) init();
	const showHardcodedNotification = ref(false);

	const questionSchemaSpecializationOptions = ref<{
		newNotificationId: string;
		templateTaskId: string;
		conceptAttrId: string;
		results: string[];
	}>({
		newNotificationId: "questionSchemaSpecializationNotification",
		templateTaskId: "f0da3efc-75e2-4631-b1d8-88dd51e08bd3",
		conceptAttrId: "71a87394-96cc-42d5-a7e9-f4d644fd14d4",
		results: [],
	});
	const hardcodedNotification = ref<DescriptorNotification & { timestamp: number }>({
		id: questionSchemaSpecializationOptions.value.newNotificationId,
		reviewRequired: false,
		layer: "descriptor",
		type: "notification",
		display: "modal",
		regarding: "",
		icon: "",
		color: "",
		label: "Frageschema Spezialisierung",
		// description:
		// 	"Ich habe den Gesprächsverlauf analysiert und ein Fragenschema gefunden, das auf das Szenario passen könnte. Klicke auf die Schaltfläche, um in das entsprechende Frageschema zu wechseln, sobald die Fragen zu den lebensbedrohlichen Situationen abgehandelt worden sind.",
		// description:
		// 	"Auf Basis des Gesprächsverlauf schlage ich folgendes Anschluss-Frageschema vor. Klicke auf den Button, um es zu aktivieren.",
		description: symbolsStore.GUILabel("a901a868-69a1-44a6-af9b-951332f003e7"),
		timestamp: 0,
	});

	function questionSchemaSpecialization(updatedMessage: Message) {
		if (!domainStore || !questionsStore || !graphStore) init();
		let domainId_spell = "10cd7fbc-e27a-44c5-8fbc-716473f788ed";
		if (domainStore.domain.id !== domainId_spell) return;

		let notificationAlreadyExists = showHardcodedNotification.value;
		if (notificationAlreadyExists) return;

		function atLeastOneQuestionToAnalyseOpen() {
			return questionsStore.unansweredQuestions.some((question) => {
				return questionSchemaSpecializationOptions.value.templateTaskId === question.instanceTask.template;
			});
		}
		if (!atLeastOneQuestionToAnalyseOpen()) return;

		let answerText;
		if (updatedMessage.lang === domainStore.languagePreference) {
			answerText = updatedMessage.words.map((w) => w.value).join(" ");
		} else {
			const translatedValue =
				updatedMessage.translation?.[domainStore.languagePreference]?.value ||
				updatedMessage.translation?.["en"]?.value;
			answerText = translatedValue || updatedMessage.words.map((w) => w.value).join(" ");
		}

		let specializations: { enumItemId: string; term: string }[] = [];
		let conceptAttr = graphStore.concept.attribute.byId(questionSchemaSpecializationOptions.value.conceptAttrId);
		if (!conceptAttr) return;
		let datatype = graphStore.descriptor.datatype.byId(conceptAttr.datatype);
		if (!datatype) return;
		// even more temporary than temporary: hardcoded
		let hardcodedEnumitems = [{ enumItemId: "a26e4dc2-6037-4cac-87b7-d1a8a0e75e15", term: "bevorstehende geburt" }];
		specializations = hardcodedEnumitems;

		const content = `Du bist ein IT-System, das nur formale Antworten auf gestellte Fragen in der zuvor definierten Weise beantwortet. 
			Deine Aufgabe ist es herauszufinden, ob die in der <Inputtext> beschriebenen Situation einer <Spezialisierung> zuzuordnen ist.
			Dazu bekommst du eine Liste an möglichen <Spezialisierungen> und der <Inputtext> als Text.
			Jede <Spezialisierung> ist ein JSON-Objekt mit dem Feld "enumItemId" und dem Feld "term". Der "term" beschreibt die <Spezialisierung> in natürlicher Sprache.
			Antworte im JSON-Format mit dem Feld "result" und dem Wert "enumItemId" den <Spezialisierungen>, die am besten passen (Array).
			Falls der <Inputtext> zu keiner der <Spezialiserungen> passt, antworte mit dem Feld "result" und dem Wert "[]" (leeres Array).

			Beispiele:
			Spezialisierungen für alle Beispiele: [{"enumItemId": "1111-1111-1111-1111", "term": "bevorstehende Geburt"}, {"enumItemId": "2222-2222-2222-2222", "term": "Sportunfall"}]
			Inputtext: "Ich bin beim Joggen über eine Wurzel gestolpert und habe mir das Knie aufgeschlagen."
			{"result": ["2222-2222-2222-2222"]}

			Inputtext: "Bei einer Frau ist die Fruchtblase geplatzt und sie hat Wehen."
			{"result": ["1111-1111-1111-1111"]}

			Inputtext: "Ich habe gerade ein Kind geboren."
			{"result": []}

			Inputtext: "Ich bin in der zwölften Woche schwanger und habe Bauchschmerzen."
			{"result": []}

			Dir stehen folgende Inputs zur Verfügung:
			Spezialisierungen: ${JSON.stringify(specializations)}
			Inputtext:  "${answerText}"
		`;
		api()
			.gpt()
			.post({
				model: "gpt-4.1",
				messages: [
					{
						content,
						role: "system",
					},
				],
				internal_type: "questionSchemaSpecialization",
				temperature: 0.5,
				jsonMode: true,
			})
			.then((r) => {
				const gptRes = r;
				try {
					let answerJSON = JSON.parse(gptRes);
					if (typeof answerJSON !== "object") throw new Error("GPT Answer is not an object");
					if (!answerJSON.result) throw new Error("GPT Answer is missing result field");

					// if the result is an array with at least one element, show the notification
					if (Array.isArray(answerJSON.result) && answerJSON.result.length > 0) {
						questionSchemaSpecializationOptions.value.results = answerJSON.result;
						showHardcodedNotification.value = true;
						hardcodedNotification.value.timestamp = Date.now();
					}
				} catch (error) {
					console.error("Error parsing GPT response: ", error);
				}
			})
			.catch((err) => console.warn(err));
	}

	return {
		questionSchemaSpecializationOptions,
		showHardcodedNotification,
		hardcodedNotification,
		questionSchemaSpecialization,
	};
});
